import {
  AiContentExtension,
  DoublePurchaseExtension,
  DoublePurchaseModule,
  EclipseOfferingApiExtension,
  PlanRecommendationExtension,
  SwitchProductModule,
} from '@wix/package-picker-sdk';
import {
  ECLIPSE_PRODUCT_TYPE_ID,
  getSearchParams,
  P3Extension,
} from '@wix/package-picker-shared';
import {
  createModuleProviderExtension,
  EclipseBiEventsExtension,
  EclipseContextExtension,
  EclipseErrorsExtension,
  EclipseExperimentsExtension,
  EclipseModalsExtension,
  EclipsePackagePickerSteps,
  EclipseTranslationsExtension,
  DealerDescriptionsPrefetch,
  AiContentPrefetch,
  ModuleContainer,
  NonBlockerWrapper,
  PackagePickerStoreExtension,
  EclipseAiRecommendationStoreExtension,
  RouteGuard,
  RouterExtension,
  useResellerBranding,
} from '@wix/package-picker-shared-eclipse';
import { ApisClientProvider } from '@wix/premium-fed-sdk/@apis-infra-react';
import { createPremiumModule } from '@wix/premium-fed-sdk/@premium-module';
import { PremiumPurchasePlatformContext } from '@wix/premium-purchase-platform/@sdk';
import React, { PropsWithChildren, useMemo } from 'react';
import { Application } from '../components/Application';
import { CheckoutPage } from '../pages/CheckoutPage/CheckoutPage';
import { SelectCyclePage } from '../pages/SelectCyclePage/SelectCyclePage';
import { SelectPlanPage } from '../pages/SelectPlanPage/SelectPlanPage';
import { LazyComponents, LazyPages } from './sdk-exports';

const routes = new Map<EclipsePackagePickerSteps, React.FC>([
  [
    EclipsePackagePickerSteps.SELECT_PLAN,
    () => (
      <RouteGuard
        step={EclipsePackagePickerSteps.SELECT_PLAN}
        Element={SelectPlanPage}
      />
    ),
  ],
  [
    EclipsePackagePickerSteps.SELECT_CYCLE,
    () => (
      <RouteGuard
        step={EclipsePackagePickerSteps.SELECT_CYCLE}
        Element={SelectCyclePage}
      />
    ),
  ],
  [
    EclipsePackagePickerSteps.CHECKOUT,
    () => (
      <RouteGuard
        step={EclipsePackagePickerSteps.CHECKOUT}
        Element={CheckoutPage}
      />
    ),
  ],
  [
    EclipsePackagePickerSteps.ANY,
    () => (
      <RouteGuard
        step={EclipsePackagePickerSteps.ANY}
        Element={SelectPlanPage}
      />
    ),
  ],
]);

const defaultP3Context: PremiumPurchasePlatformContext = {
  productTypeId: ECLIPSE_PRODUCT_TYPE_ID,
  purchaseContext: {
    purchaseType: 'sunrise',
    overrideSubscriptionId: getSearchParams().overrideSubscriptionId,
  },
};

// Container for lazy module binding - set after createPremiumModule completes
const moduleContainer: ModuleContainer = { module: null };

export const EclipsePackagePickerModule = createPremiumModule(
  'eclipse-package-picker-module',
  [
    // ⚠️ DO NOT TOUCH - createModuleProviderExtension MUST BE FIRST ⚠️
    createModuleProviderExtension(moduleContainer),
    EclipseBiEventsExtension,
    EclipseContextExtension,
    EclipseTranslationsExtension,
    EclipseExperimentsExtension,
    EclipseErrorsExtension({ notificationButtonType: 'button' }),
    PackagePickerStoreExtension,
    P3Extension(defaultP3Context),
    SwitchProductModule.SDK.SwitchExtension,
    DoublePurchaseExtension,
    RouterExtension(routes),
    EclipseOfferingApiExtension,
    AiContentExtension,
    PlanRecommendationExtension,
    EclipseModalsExtension,
    EclipseAiRecommendationStoreExtension,
  ],
  (module) => {
    const { siteGuid, isDowngradeEnabled } = getSearchParams();

    return {
      SDK: {
        Application,
        Components: LazyComponents,
        Pages: LazyPages,
      },
      Provider: ({ children }: PropsWithChildren<{}>) => {
        const { purchaseContextOverride } = module.internals.useContext();
        const { isResellerOffering } = useResellerBranding();

        const p3Context = useMemo(
          () => ({
            ...defaultP3Context,
            purchaseContext: {
              ...defaultP3Context.purchaseContext,
              ...purchaseContextOverride,
              params: {
                ...defaultP3Context.purchaseContext.params,
                ...purchaseContextOverride.params,
              },
            },
          }),
          [purchaseContextOverride],
        );

        SwitchProductModule.SDK.switchContext.set('p3Context', p3Context);
        module.SDK.eclipseOfferingContext.set('p3Context', p3Context);
        module.SDK.eclipseOfferingContext.set(
          'isResellerOffering',
          isResellerOffering,
        );

        return (
          <DoublePurchaseModule.Provider
            context={{
              siteGuid,
              productTypeId: ECLIPSE_PRODUCT_TYPE_ID,
            }}
          >
            <SwitchProductModule.Provider
              context={{
                siteGuid,
                isDowngradeEnabled,
                p3Context,
              }}
            >
              <ApisClientProvider useErrorBoundary={true}>
                <NonBlockerWrapper componentName="DealerDescriptionsPrefetch">
                  <DealerDescriptionsPrefetch />
                </NonBlockerWrapper>
                <NonBlockerWrapper componentName="AiContentPrefetch">
                  <AiContentPrefetch />
                </NonBlockerWrapper>
                <module.SDK.Modals />
                {children}
              </ApisClientProvider>
            </SwitchProductModule.Provider>
          </DoublePurchaseModule.Provider>
        );
      },
    };
  },
);

// Set the module after creation for the ModuleProvider to access
moduleContainer.module = EclipsePackagePickerModule;
